import React, { useState, useEffect } from 'react';
import Layout from '@/components/Layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Link } from 'react-router-dom';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Trash2, CheckSquare, Square, Grid, List, MapPin, Eye, Edit } from 'lucide-react';
import PostPreview from '@/components/Posts/PostPreview';
import CategoryBadge from '@/components/Posts/CategoryBadge';
import PostsTable from '@/components/Posts/PostsTable';
import { useLocationExtraction } from '@/hooks/useLocationExtraction';

interface BlogPost {
  id: string;
  title: string;
  content: string;
  excerpt: string | null;
  featured_image: string | null;
  slug: string;
  author: string;
  published_date: string | null;
  status: 'draft' | 'published';
  seo_title: string | null;
  seo_description: string | null;
  seo_keywords: string[] | null;
  categories: string[] | null;
  tags: string[] | null;
  created_at: string | null;
  updated_at: string | null;
}

const Posts = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'published' | 'draft'>('all');
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDrafts, setSelectedDrafts] = useState<Set<string>>(new Set());
  const [isPublishing, setIsPublishing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');

  const { extractLocationInfo } = useLocationExtraction();

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      console.log('Posts: Starting optimized fetch...');
      setError(null);
      
      // Check auth state first
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      console.log('Posts: Session check:', session?.user?.email || 'no session');
      
      if (sessionError) {
        throw new Error(`Session error: ${sessionError.message}`);
      }
      
      if (!session) {
        throw new Error('No active session found');
      }

      // Optimized query - only select necessary fields for list view
      const { data, error } = await supabase
        .from('blog_posts')
        .select(`
          id,
          title,
          excerpt,
          author,
          published_date,
          status,
          categories,
          tags,
          created_at,
          content
        `)
        .order('created_at', { ascending: false })
        .limit(100); // Limit to reduce disk IO

      console.log('Posts: Query response:', { 
        dataCount: data?.length, 
        error: error?.message 
      });

      if (error) {
        console.error('Posts: Supabase error:', error);
        throw error;
      }
      
      const typedPosts = (data || []).map(post => ({
        ...post,
        status: post.status as 'draft' | 'published',
        // Add missing fields with defaults for compatibility
        featured_image: null,
        slug: '',
        seo_title: null,
        seo_description: null,
        seo_keywords: null,
        updated_at: null
      }));
      
      console.log('Posts: Successfully loaded', typedPosts.length, 'posts');
      setPosts(typedPosts);
    } catch (error: any) {
      console.error('Posts: Error fetching posts:', error);
      setError(error.message || 'Failed to load blog posts');
      toast.error(`Failed to load blog posts: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const generateLocationSlug = (post: BlogPost, locationName: string | null) => {
    if (!locationName) {
      return post.slug || 'untitled';
    }

    const locationSlug = locationName.toLowerCase().replace(/\s+/g, '-');
    const category = post.categories?.[0]?.toLowerCase().replace(/\s+/g, '-') || 'roofing';
    
    let titleSlug = post.title
      .toLowerCase()
      .replace(new RegExp(locationName.toLowerCase(), 'gi'), '{name}')
      .replace(/[^a-z0-9\s-{}\-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();

    return `${locationSlug}/${category}/${titleSlug}`;
  };

  const getPostType = (post: BlogPost) => {
    const locationInfo = extractLocationInfo(post);
    return locationInfo ? 'location' : 'template';
  };

  const handleDeletePost = async (id: string) => {
    if (!confirm('Are you sure you want to delete this post?')) return;

    try {
      const { error } = await supabase
        .from('blog_posts')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setPosts(posts.filter(post => post.id !== id));
      setSelectedDrafts(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
      toast.success('Post deleted successfully');
    } catch (error) {
      console.error('Error deleting post:', error);
      toast.error('Failed to delete post');
    }
  };

  const handleDeleteAllDrafts = async () => {
    const draftPosts = posts.filter(post => post.status === 'draft');
    
    if (draftPosts.length === 0) {
      toast.info('No draft posts to delete');
      return;
    }

    if (!confirm(`Are you sure you want to delete all ${draftPosts.length} draft posts? This action cannot be undone.`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('blog_posts')
        .delete()
        .eq('status', 'draft');

      if (error) throw error;

      setPosts(posts.filter(post => post.status === 'published'));
      setSelectedDrafts(new Set());
      toast.success(`Successfully deleted ${draftPosts.length} draft posts`);
    } catch (error) {
      console.error('Error deleting draft posts:', error);
      toast.error('Failed to delete draft posts');
    }
  };

  const handleToggleStatus = async (id: string, currentStatus: 'draft' | 'published') => {
    const newStatus = currentStatus === 'published' ? 'draft' : 'published';
    
    try {
      const updateData: any = { status: newStatus };
      
      if (newStatus === 'published') {
        updateData.published_date = new Date().toISOString();
      }

      const { error } = await supabase
        .from('blog_posts')
        .update(updateData)
        .eq('id', id);

      if (error) throw error;

      setPosts(posts.map(post => 
        post.id === id ? { 
          ...post, 
          status: newStatus, 
          published_date: newStatus === 'published' ? new Date().toISOString() : post.published_date 
        } : post
      ));
      
      if (newStatus === 'published') {
        setSelectedDrafts(prev => {
          const newSet = new Set(prev);
          newSet.delete(id);
          return newSet;
        });
      }
      
      toast.success(`Post ${newStatus === 'published' ? 'published' : 'saved as draft'}`);
    } catch (error) {
      console.error('Error updating post status:', error);
      toast.error('Failed to update post status');
    }
  };

  const handleSelectDraft = (postId: string, checked: boolean) => {
    setSelectedDrafts(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(postId);
      } else {
        newSet.delete(postId);
      }
      return newSet;
    });
  };

  const handleSelectAllDrafts = () => {
    const draftPosts = posts.filter(post => post.status === 'draft');
    const allDraftsSelected = draftPosts.length > 0 && draftPosts.every(post => selectedDrafts.has(post.id));
    
    if (allDraftsSelected) {
      setSelectedDrafts(new Set());
    } else {
      setSelectedDrafts(new Set(draftPosts.map(post => post.id)));
    }
  };

  const handlePublishSelected = async () => {
    if (selectedDrafts.size === 0) {
      toast.info('No drafts selected for publishing');
      return;
    }

    if (!confirm(`Are you sure you want to publish ${selectedDrafts.size} selected posts?`)) {
      return;
    }

    setIsPublishing(true);
    try {
      const updateData = {
        status: 'published' as const,
        published_date: new Date().toISOString()
      };

      const { error } = await supabase
        .from('blog_posts')
        .update(updateData)
        .in('id', Array.from(selectedDrafts));

      if (error) throw error;

      setPosts(posts.map(post => 
        selectedDrafts.has(post.id) ? { 
          ...post, 
          status: 'published' as const, 
          published_date: updateData.published_date 
        } : post
      ));

      setSelectedDrafts(new Set());
      toast.success(`Successfully published ${selectedDrafts.size} posts`);
    } catch (error) {
      console.error('Error publishing selected posts:', error);
      toast.error('Failed to publish selected posts');
    } finally {
      setIsPublishing(false);
    }
  };

  const searchQuery = searchTerm.toLowerCase();
  const filteredPosts = searchQuery
    ? posts.filter(post => 
        post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.excerpt?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.categories?.some(cat => cat.toLowerCase().includes(searchQuery.toLowerCase()))
      )
    : posts;

  const statusFilteredPosts = statusFilter === 'all' 
    ? filteredPosts 
    : filteredPosts.filter(post => post.status === statusFilter);

  const draftCount = posts.filter(post => post.status === 'draft').length;
  const draftPosts = posts.filter(post => post.status === 'draft');
  const allDraftsSelected = draftPosts.length > 0 && draftPosts.every(post => selectedDrafts.has(post.id));

  if (isLoading) {
    return (
      <Layout>
        <div className="container py-8">
          <div className="flex justify-center items-center min-h-[400px]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading blog posts...</p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="container py-8">
          <div className="flex justify-center items-center min-h-[400px]">
            <div className="text-center">
              <div className="text-red-500 mb-4">
                <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-medium mb-2">Failed to Load Posts</h3>
              <p className="text-muted-foreground mb-4">{error}</p>
              <Button onClick={() => {
                setIsLoading(true);
                fetchPosts();
              }}>
                Try Again
              </Button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container py-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold">Blog Posts</h1>
            <p className="text-muted-foreground mt-1">
              Manage and organize your blog content ({posts.length} total)
            </p>
          </div>
          <div className="flex gap-2 mt-4 md:mt-0">
            {draftCount > 0 && (
              <>
                <Button 
                  variant="outline" 
                  onClick={handleSelectAllDrafts}
                  className="flex items-center gap-2"
                >
                  {allDraftsSelected ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
                  {allDraftsSelected ? 'Unselect All' : 'Select All Drafts'}
                </Button>
                
                {selectedDrafts.size > 0 && (
                  <Button 
                    onClick={handlePublishSelected}
                    disabled={isPublishing}
                    className="flex items-center gap-2"
                  >
                    {isPublishing ? 'Publishing...' : `Publish Selected (${selectedDrafts.size})`}
                  </Button>
                )}
                
                <Button 
                  variant="destructive" 
                  onClick={handleDeleteAllDrafts}
                  className="flex items-center gap-2"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete All Drafts ({draftCount})
                </Button>
              </>
            )}
            <Button asChild>
              <Link to="/create-post">Create New Post</Link>
            </Button>
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <Input
            placeholder="Search posts..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="md:max-w-xs"
          />
          <Select value={statusFilter} onValueChange={(value) => setStatusFilter(value as 'all' | 'published' | 'draft')}>
            <SelectTrigger className="w-full md:w-40">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Posts</SelectItem>
              <SelectItem value="published">Published</SelectItem>
              <SelectItem value="draft">Drafts</SelectItem>
            </SelectContent>
          </Select>
          
          <div className="flex items-center space-x-2">
            <Label htmlFor="view-mode">View:</Label>
            <div className="flex items-center gap-2">
              <Button
                variant={viewMode === 'grid' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('grid')}
              >
                <Grid className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === 'table' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('table')}
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {statusFilteredPosts.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">
              {posts.length === 0 
                ? 'No posts found. Create your first blog post to get started.' 
                : 'No posts match your search criteria.'
              }
            </p>
            <Button asChild className="mt-4">
              <Link to="/create-post">Create New Post</Link>
            </Button>
          </div>
        ) : viewMode === 'table' ? (
          <Card>
            <CardContent className="p-0">
              <PostsTable
                posts={statusFilteredPosts}
                selectedDrafts={selectedDrafts}
                onSelectDraft={handleSelectDraft}
                onToggleStatus={handleToggleStatus}
                onDeletePost={handleDeletePost}
                extractLocationInfo={extractLocationInfo}
                generateLocationSlug={generateLocationSlug}
              />
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {statusFilteredPosts.map(post => {
              const locationInfo = extractLocationInfo(post);
              const postType = getPostType(post);
              const locationSlug = generateLocationSlug(post, locationInfo);
              
              console.log(`Post "${post.title}": locationInfo="${locationInfo}", postType="${postType}", slug="${locationSlug}"`);
              
              return (
                <Card key={post.id} className="flex flex-col">
                  {post.featured_image && (
                    <div className="aspect-video w-full overflow-hidden">
                      <img
                        src={post.featured_image}
                        alt={post.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex gap-2 items-center">
                        {post.status === 'draft' && (
                          <Checkbox
                            checked={selectedDrafts.has(post.id)}
                            onCheckedChange={(checked) => handleSelectDraft(post.id, checked as boolean)}
                            className="mr-2"
                          />
                        )}
                        <Badge variant={post.status === 'published' ? 'default' : 'outline'}>
                          {post.status === 'published' ? 'Published' : 'Draft'}
                        </Badge>
                        {postType === 'location' && locationInfo && (
                          <Badge variant="secondary" className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {locationInfo}
                          </Badge>
                        )}
                        {postType === 'template' && (
                          <Badge variant="outline" className="text-purple-600 border-purple-200">
                            Template
                          </Badge>
                        )}
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {post.published_date ? new Date(post.published_date).toLocaleDateString() : 'No date'}
                      </span>
                    </div>
                    <PostPreview
                      title={post.title}
                      excerpt={post.excerpt || ''}
                      content={post.content}
                      locationName={locationInfo}
                      author={post.author}
                      categories={post.categories}
                      tags={post.tags}
                      seoTitle={post.seo_title}
                      seoDescription={post.seo_description}
                      seoKeywords={post.seo_keywords}
                    />
                    <CardDescription>{post.author}</CardDescription>
                    
                    {/* Display the generated URL */}
                    <div className="text-xs text-muted-foreground mt-2 p-2 bg-muted/50 rounded">
                      <span className="font-medium">URL:</span> /blog/{locationSlug}
                    </div>
                  </CardHeader>
                  <CardContent className="pb-2 flex-grow">
                    <div className="flex flex-wrap gap-1 mt-3">
                      {post.categories?.slice(0, 3).map((category, index) => (
                        <CategoryBadge 
                          key={index} 
                          category={category}
                          locationName={locationInfo}
                        />
                      ))}
                      {(post.categories?.length || 0) > 3 && (
                        <Badge variant="secondary" className="text-xs">
                          +{(post.categories?.length || 0) - 3} more
                        </Badge>
                      )}
                    </div>
                  </CardContent>
                  <CardFooter className="pt-2">
                    <div className="flex justify-between w-full gap-2">
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" asChild>
                          <Link to={`/post-preview/${post.id}`}>
                            <Eye className="h-4 w-4" />
                          </Link>
                        </Button>
                        <Button variant="outline" size="sm" asChild>
                          <Link to={`/create-post?edit=${post.id}`}>
                            <Edit className="h-4 w-4" />
                          </Link>
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleToggleStatus(post.id, post.status)}
                        >
                          {post.status === 'published' ? 'Unpublish' : 'Publish'}
                        </Button>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleDeletePost(post.id)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardFooter>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Posts;
